import type { ApiRequestHelpers } from "./useHttpClient";
import type {
  AvailabilityResponseArray,
  BookableTreatment,
  CreatePatientIdentifierPayload,
  CreatePatientResponse,
  CreateTreatmentAppointmentPayload,
  EmailPreferences,
  EmbedConfigurationFull,
  EmptyResponse,
  FilterByQuery,
  IncludeQuery,
  InsuranceIdentifier,
  InsuranceReference,
  Intake,
  IntakeQuestionnaireDelivery,
  LocationIdentifier,
  MatchWebPlannerAppointmentPayload,
  PaginationQuery,
  Patient,
  PatientConsent,
  PatientIdentifier,
  PatientIdentifierData,
  PatientEmailCommunication,
  PatientIdentifierProposalData,
  PatientInclude,
  PatientMatchCandidate,
  PatientProfile,
  PatientReference,
  PatientTreatmentCountMail,
  PatientWebPlannerAppointment,
  PatientWebPlannerAppointmentPayload,
  PaginationResponse,
  ProposePatientIdentifierChangePayload,
  Response,
  SimpleQuery,
  SortByQuery,
  SubscriptionSubscriber,
  Treatment,
  TreatmentAppointment,
  TreatmentAvailabilityDay,
  TreatmentIdentifier,
  UpdateEmailPreferencesPayload,
  UpdateInsuranceEndDatePayload,
  UpdatePatientIdentifierPayload,
  UpdatePatientProfilePayload,
  UpsertPatientConsentPayload,
  UserIdentifier,
  WebPlannerAppointment,
} from "~/types/api";

export const serializeBooleanForFormData = (value: boolean | null | undefined): string => {
  return value ? "1" : "0";
};

export function createPatientApi({
  getRequest,
  postRequest,
  putRequest,
  patchRequest,
  getPaginationRequest,
}: ApiRequestHelpers) {
  /** Fetches a paginated list of patients across all locations. */
  const patients = (
    pagination: PaginationQuery,
    sortBy?: SortByQuery,
    includes: PatientInclude[] = [],
    filterBy?: FilterByQuery,
  ) => getPaginationRequest<PatientReference>("/patients/new", pagination, sortBy, includes, filterBy);

  /** Fetches a paginated list of patients scoped to a specific location. */
  const locationPatients = (
    locationId: number,
    pagination: PaginationQuery,
    sortBy?: SortByQuery,
    includes: PatientInclude[] = [],
    filterBy?: FilterByQuery,
  ) => getPaginationRequest<Patient>(`/locations/${locationId}/patients`, pagination, sortBy, includes, filterBy);

  /** Fetches a single patient by ID. */
  const patient = (patientId: PatientIdentifier, query?: SimpleQuery) =>
    getRequest<Response<Patient>>(`/patients/${patientId}`, {
      params: query,
    });

  /** Fetches a patient by their associated user ID. */
  const patientByUserId = (userId: UserIdentifier) => getRequest<Response<Patient>>(`/patients/by-user/${userId}`);

  /** Fetches a paginated list of treatments for a patient. */
  const patientTreatments = (
    patientProfileId: PatientIdentifier,
    pagination: PaginationQuery = { number: 1, size: 25 },
    sortBy?: SortByQuery,
    includes?: IncludeQuery,
    filters?: FilterByQuery,
  ) =>
    getPaginationRequest<Treatment>(`/patients/${patientProfileId}/treatments`, pagination, sortBy, includes, filters);

  /** Fetches all intakes for a patient. */
  const patientIntakes = (patientId: PatientIdentifier) =>
    getRequest<Response<Intake[]>>(`/patients/${patientId}/intakes`);

  /** Fetches the patient's active treatments that are eligible for self-service booking. */
  const bookableTreatments = (patientId: PatientIdentifier) =>
    getRequest<Response<BookableTreatment[]>>(`/patients/${patientId}/treatments/bookable`);

  /** Fetches real availability for a physio who previously treated the patient on this treatment. */
  const treatmentAvailability = (
    patientId: PatientIdentifier,
    treatmentId: TreatmentIdentifier,
    physioId: UserIdentifier,
    from: string,
    to: string,
  ) =>
    getRequest<Response<TreatmentAvailabilityDay[]>>(`/patients/${patientId}/treatments/${treatmentId}/availability`, {
      params: { physio_id: physioId, from, to },
    });

  /** Books a follow-up appointment on an existing treatment, reusing its performance code. */
  const createTreatmentAppointment = (
    patientId: PatientIdentifier,
    treatmentId: TreatmentIdentifier,
    data: CreateTreatmentAppointmentPayload,
  ) =>
    postRequest<Response<TreatmentAppointment>>(`/patients/${patientId}/treatments/${treatmentId}/appointments`, data);

  /** Fetches the active webplanner configuration for the patient's current location, or null if none is configured. */
  const patientWebPlannerConfig = (patientId: PatientIdentifier) =>
    getRequest<Response<EmbedConfigurationFull | null>>(`/patients/${patientId}/webplanner-config`);

  /** Fetches real webplanner availability for the patient's current location's configured webplanner. */
  const patientWebPlannerAvailability = (
    patientId: PatientIdentifier,
    from: string,
    to: string,
    locationIds: number[],
    productId: number,
    physioIds?: number[],
    specialisms?: string[],
  ) =>
    getRequest<Response<AvailabilityResponseArray>>(`/patients/${patientId}/webplanner-availability`, {
      params: {
        location_ids: locationIds,
        product_id: productId,
        from,
        to,
        user_ids: physioIds,
        specialisms,
      },
    });

  /** Books a new-treatment (intake) appointment from the patient's current location's webplanner configuration. */
  const createPatientWebPlannerAppointment = (
    patientId: PatientIdentifier,
    data: PatientWebPlannerAppointmentPayload,
  ) => postRequest<Response<PatientWebPlannerAppointment>>(`/patients/${patientId}/webplanner-appointments`, data);

  /** Fetches or updates a patient's profile depending on whether data is provided. */
  const patientProfile = (patientId: PatientIdentifier, data?: UpdatePatientProfilePayload) =>
    data
      ? putRequest<Response<PatientProfile>>(`/patients/${patientId}/profile`, data)
      : getRequest<Response<PatientProfile>>(`/patients/${patientId}/profile`);

  /** Updates only a patient's email address. */
  const updatePatientEmail = (patientId: PatientIdentifier, email: string) =>
    patchRequest<Response<PatientProfile>>(`/patients/${patientId}/profile/email`, { email });

  const updateInsuranceEndDate = (
    locationId: LocationIdentifier,
    patientId: PatientIdentifier,
    insuranceId: InsuranceIdentifier,
    payload: UpdateInsuranceEndDatePayload,
  ) =>
    patchRequest<Response<InsuranceReference>>(
      `/locations/${locationId}/patients/${patientId}/insurances/${insuranceId}/end-date`,
      payload,
    );

  /** Fetches a patient's email notification preferences. */
  const emailPreferences = (patientId: PatientIdentifier) =>
    getRequest<Response<EmailPreferences>>(`/patients/${patientId}/email-preferences`);

  /** Updates a patient's email notification preferences. */
  const updateEmailPreferences = (patientId: PatientIdentifier, data: UpdateEmailPreferencesPayload) =>
    putRequest<Response<EmailPreferences>>(`/patients/${patientId}/email-preferences`, data);

  /** Whether an intake at the location sends the questionnaires of the complaint region, and if not, why. */
  const intakeQuestionnaireDelivery = (patientId: PatientIdentifier, locationId: number) =>
    getRequest<Response<IntakeQuestionnaireDelivery>>(`/patients/${patientId}/intake-questionnaires`, {
      params: { location_id: locationId },
    });

  /** Creates or updates a patient's consent record. */
  const upsertPatientConsent = (patientId: PatientIdentifier, data: UpsertPatientConsentPayload) =>
    patchRequest<Response<PatientConsent>>(`/patients/${patientId}/consent`, data);

  /** Creates a new patient with personal details, address, and optional identifiers. */
  const createPatient = (data: {
    first_name: string;
    last_name: string;
    initials: string;
    gender: string;
    birth_date: string;
    email: string | null;
    infix: string;
    phone_number: string | null;
    citizen_service_number: string | null;
    street: string;
    housenumber: number;
    housenumber_addition: string | null;
    postal_code: string;
    city: string;
    country: string;
    has_dutch_insurance?: boolean;
    nationality?: string | null;
    preferred_language?: string | null;
    identifiers?: Array<{
      type: string;
      value: string;
      country_iso3: string;
      valid_from?: string | null;
      valid_until?: string | null;
      include_on_invoice?: boolean;
    }>;
    given_name?: string | null;
    /** Location the patient is created at (their primary location). Omitted: the current location. */
    location_id?: number | null;
  }) => {
    const formData = new URLSearchParams();
    if (data.location_id) formData.append("location_id", String(data.location_id));
    formData.append("first_name", data.first_name || "");
    formData.append("last_name", data.last_name);
    formData.append("initials", data.initials);
    formData.append("gender", data.gender);
    formData.append("infix", data.infix);
    formData.append("dob", data.birth_date);
    if (data.email) formData.append("email", data.email);
    if (data.given_name) formData.append("given_name", data.given_name);
    if (data.nationality) formData.append("nationality", data.nationality);
    if (data.preferred_language) formData.append("preferred_language", data.preferred_language);
    formData.append("timezone", "Europe/Amsterdam");
    formData.append("phone_number", data.phone_number || "");
    formData.append("has_dutch_insurance", serializeBooleanForFormData(data.has_dutch_insurance ?? true));
    if (data.citizen_service_number) formData.append("citizen_service_number", data.citizen_service_number);
    formData.append("address[street]", data.street);
    formData.append("address[street_number]", data.housenumber.toString());
    formData.append("address[street_number_addition]", data.housenumber_addition || "");
    formData.append("address[postal_code]", data.postal_code);
    formData.append("address[city]", data.city);
    formData.append("address[country]", data.country);

    data.identifiers?.forEach((identifier, index) => {
      formData.append(`identifiers[${index}][type]`, identifier.type);
      formData.append(`identifiers[${index}][value]`, identifier.value);
      formData.append(`identifiers[${index}][country_iso3]`, identifier.country_iso3);
      if (identifier.valid_from) formData.append(`identifiers[${index}][valid_from]`, identifier.valid_from);
      if (identifier.valid_until) formData.append(`identifiers[${index}][valid_until]`, identifier.valid_until);
      formData.append(
        `identifiers[${index}][include_on_invoice]`,
        serializeBooleanForFormData(identifier.include_on_invoice ?? false),
      );
    });

    return postRequest<CreatePatientResponse>(`/patients`, formData);
  };

  /** Archives a patient by ID. */
  const archivePatient = (patientId: PatientIdentifier) => putRequest<EmptyResponse>(`/patients/${patientId}`);

  /** Fetches treatment count mail history for a patient. */
  const patientTreatmentCountMail = (patientId: string) =>
    getRequest<PaginationResponse<PatientTreatmentCountMail>>(`/patients/${patientId}/treatment-count/mail`);

  /** Fetches a paginated list of emails sent to a patient. */
  const fetchPatientEmailCommunications = (
    patientId: PatientIdentifier,
    pagination: PaginationQuery,
    filterBy?: FilterByQuery,
  ) =>
    getPaginationRequest<PatientEmailCommunication>(
      `/patients/${patientId}/email-communications`,
      pagination,
      undefined,
      undefined,
      filterBy,
    );

  /** Fetches all subscriptions a patient is enrolled in. */
  const getPatientSubscriptions = (patientId: number) =>
    getRequest<Response<SubscriptionSubscriber[]>>(`/patients/${patientId}/subscriptions`);

  /** Fetches all patient identifiers for the current employee. */
  const patientIdentifiers = () => getRequest<Response<PatientIdentifierData[]>>("/identifiers").then((r) => r.data);

  /** Fetches pending patient identifier proposals for the current employee. */
  const patientIdentifierProposals = () =>
    getRequest<Response<PatientIdentifierProposalData[]>>("/identifiers/proposals").then((r) => r.data);

  /** Updates a patient identifier. */
  const updatePatientIdentifier = (identifierId: string, payload: UpdatePatientIdentifierPayload) =>
    putRequest<Response<PatientIdentifierData>>(`/identifiers/${identifierId}`, payload).then((r) => r.data);

  /** Accepts a pending identifier proposal. */
  const acceptIdentifierProposal = (proposalId: string) =>
    postRequest<void>(`/identifiers/proposals/${proposalId}/accept`, {});

  /** Rejects a pending identifier proposal. */
  const rejectIdentifierProposal = (proposalId: string) =>
    postRequest<void>(`/identifiers/proposals/${proposalId}/reject`, {});

  /** Fetches identifiers for a specific patient (employee scope). */
  const employeePatientIdentifiers = (patientId: string) =>
    getRequest<Response<PatientIdentifierData[]>>(`/patients/${patientId}/identifiers`).then((r) => r.data);

  /** Creates a new identifier for a patient. */
  const createPatientIdentifier = (patientId: string, payload: CreatePatientIdentifierPayload) =>
    postRequest<Response<PatientIdentifierData>>(`/patients/${patientId}/identifiers`, payload).then((r) => r.data);

  /** Proposes a change to an existing patient identifier. */
  const proposePatientIdentifierChange = (
    patientId: string,
    identifierId: string,
    payload: ProposePatientIdentifierChangePayload,
  ) => postRequest<void>(`/patients/${patientId}/identifiers/${identifierId}/proposals`, payload);

  /** Withdraws a pending identifier proposal for a patient. */
  const withdrawIdentifierProposal = (patientId: string, proposalId: string) =>
    postRequest<void>(`/patients/${patientId}/identifiers/proposals/${proposalId}/withdraw`, {});

  /** Fetches all unresolved web planner appointment submissions across the current organization. */
  const fetchWebPlannerAppointments = () => getRequest<Response<WebPlannerAppointment[]>>("/webplanner-appointments");

  /** Fetches ranked candidate matches for a web planner appointment submission. */
  const fetchWebPlannerAppointmentMatches = (submissionId: number) =>
    getRequest<Response<PatientMatchCandidate[]>>(`/webplanner-appointments/${submissionId}/matches`);

  /** Matches a web planner appointment submission to an existing, already-verified patient. */
  const matchWebPlannerAppointment = (submissionId: number, payload: MatchWebPlannerAppointmentPayload) =>
    postRequest<Response<{ user_id: UserIdentifier }>>(`/webplanner-appointments/${submissionId}/match`, payload);

  return {
    patients,
    locationPatients,
    patient,
    patientByUserId,
    patientTreatments,
    patientIntakes,
    bookableTreatments,
    treatmentAvailability,
    createTreatmentAppointment,
    patientWebPlannerConfig,
    patientWebPlannerAvailability,
    createPatientWebPlannerAppointment,
    patientProfile,
    updatePatientEmail,
    updateInsuranceEndDate,
    emailPreferences,
    updateEmailPreferences,
    intakeQuestionnaireDelivery,
    upsertPatientConsent,
    createPatient,
    archivePatient,
    patientTreatmentCountMail,
    fetchPatientEmailCommunications,
    getPatientSubscriptions,
    patientIdentifiers,
    patientIdentifierProposals,
    updatePatientIdentifier,
    acceptIdentifierProposal,
    rejectIdentifierProposal,
    employeePatientIdentifiers,
    createPatientIdentifier,
    proposePatientIdentifierChange,
    withdrawIdentifierProposal,
    fetchWebPlannerAppointments,
    fetchWebPlannerAppointmentMatches,
    matchWebPlannerAppointment,
  };
}

export type PatientApi = ReturnType<typeof createPatientApi>;
